<script>
  import { mapState } from 'vuex'
  import Alerts from '@/components/Alerts'
  import AllModals from '@/components/Modals/AllModals'
  import ModalEditAlternativeEmail from '@/components/Modals/ModalEditAlternativeEmail'
  import PrivateHeader from '@/components/PrivateHeader'
  import ChallengeBox from '@/components/ChallengeBox'
  import AppOffline from '@/components/AppOffline'
  import AppFeedback from '@/components/AppFeedback'

  export default {
    name: 'app',

    components: {
      Alerts,
      AllModals,
      PrivateHeader,
      ChallengeBox,
      AppOffline,
      AppFeedback,
      ModalEditAlternativeEmail
    },

    data () {
      return {}
    },

    computed: {
      ...mapState({
        global_loading: state => state.loading
      })
    },

    watch: {
      '$auth.watch.authenticated': function(val) {
        if (val) {
          this.showAlternativeEmailForm()

          document.body.classList.add('is-logged')
          window.gtag && window.gtag('set', {
            'user_id': this.user.id
          })
        } else {
          document.body.classList.remove('is-logged')
        }
      }
    },

    methods: {
      showAlternativeEmailForm() {
        if (
          this.user.player.school.enrollment_type === 'EMAIL' &&
          !this.user.alternative_email
        ) {
          this.$store.commit('Modals/show', 'edit_alternative_email')
        }
      }
    }
  }
</script>

<template>
  <div id="app">
    <template v-if="$auth.check()">
      <private-header />
      <challenge-box />
    </template>

    <alerts />
    <router-view />
    <all-modals />

    <transition leave-to-class="fadeOut" leave-active-class="animated">
      <div class="site-loading app-loader" v-if="global_loading || !$auth.ready()"></div>
    </transition>

    <app-offline />
    <app-feedback />
  </div>
</template>

<style lang="scss">
  @import '~mdi/css/materialdesignicons.min.css';
  @import '~bulma';
  @import '~buefy/src/scss/buefy';
  @import '~animate.css';
  @import '~vue-multiselect/dist/vue-multiselect.min.css';

  html {
    background-color: initial;
  }

  body {
    font-size: $font-size-s;
    background: $grey-darker;

    &.is-logged,
    .app-page-player & {
      padding-top: $app-header-height;
      padding-bottom: pxToEm(60);
    }
  }

  ::-webkit-scrollbar-track {
    background-color: $grey-lighter;
    border-radius: 10px;
  }

  ::-webkit-scrollbar {
    width: 10px;
    background-color: $grey-lighter;
  }

  ::-webkit-scrollbar-thumb {
    background-color: $red;
    border: 2px solid $grey-lighter;
    border-radius: 10px;
  }

  body::-webkit-scrollbar-track {
    background-color: $grey-darker;
  }

  body::-webkit-scrollbar  {
    background-color: $grey-darker;
  }

  body::-webkit-scrollbar-thumb {
    border-color: $grey-darker;
  }

  img {
    max-width: 100%;
  }

  small {
    font-size: $font-size-xs;
  }

  .animated {
    animation-duration: 0.6s;
  }

  .input,
  .textarea,
  .select select {
    width: 100%;
    border-top: 0;
    border-right: 0;
    border-left: 0;
    box-shadow: none;
    border-radius: 0;
    transition: 0.4s, height 0.2s;
  }

  .textarea {
    resize: none;
  }

  .select {
    &,
    select {
      display: block;
    }

    &:after {
      border-color: $grey-light;
    }

    &.is-danger {
      select,
      &:after {
        border-color: $danger;
      }
    }
  }

  .button {
    min-height: 46px;
    height: auto;
    padding: 10px 20px;
    font-weight: 500;

    &.is-text-danger {
      color: $red;
    }

    &:not(.is-text):not(.is-flat) {
      box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.2);
    }

    &.is-light {
      background-color: $grey-light;
    }

    &.is-fullwidth {
      display: block;
      width: 100%;
    }
  }

  .image {
    &.is-42x42 {
      width: 42px;
      height: 42px;
    }

    &.is-round {
      overflow: hidden;
      border-radius: $radius;
    }

    &.is-cover,
    &.is-contain {
      img {
        max-height: none;
        max-width: none;
        width: 100%;
        height: 100%;
      }
    }

    &.is-cover img {
      object-fit: cover;
    }

    &.is-contain img {
      object-fit: contain;
    }
  }

  .media {
    &.is-light {
      .title {
        color: #fff;
      }

      .subtitle {
        color: $grey;
      }
    }

    &-content {
      overflow: initial;
    }
  }

  .title:not(.is-spaced) + .subtitle {
    margin-top: pxToEm(-20);
  }

  .subtitle {
    font-weight: 500;
  }

  .panel.is-light,
  .panel.is-danger {
    overflow: hidden;
    border-radius: $radius-large;

    .panel-heading {
      font-size: $font-size-m;
      color: $grey-darker;
      border: 0;
    }

    .panel-content {
      border: 0;
      border-top: 1px solid $grey-lighter;
      background: #fff;
    }
  }

  .panel.is-light .panel-heading {
    background: #fff;
  }

  .panel.is-danger .panel-heading {
    color: #fff;
    background: $danger;
  }

  .tag {
    padding: pxToEm(7) pxToEm(15);

    &.is-unused {
      color: #fff;
      border: 1px solid #fff;
      background: none;
    }

    &.is-unused {
      color: #000;
      border-color: #000;
      background: none;
    }

    &.is-unused:hover,
    &.is-used {
      color: #fff;
      background: $red;
      border: 1px solid $red;
    }

    &.is-used,
    &.is-unused {
      cursor: pointer;
    }
  }

  .notification {
    &.is-translucide {
      color: #fff;
      background-color: rgba(255,255,255,0.2);
    }
  }

  .lazy-init {
    position: relative;
    background-color: $grey-lighter;

    &:after {
      @include loader;
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      height: 40px;
      width: 40px;
      margin-left: -20px;
      margin-top: -20px;
      border-left-color: $primary;
      border-bottom-color: $primary;
    }
  }

  .lazy-loaded {
    background-color: transparent;

    &:after {
      display: none;
    }
  }

  .app-loader {
    min-width: 250px;
    min-height: 250px;
    background: url('./assets/img/loader.gif') center center no-repeat;
  }

  .site-loading {
    position: fixed;
    z-index: 5000;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #fff;
  }

  .app-simple-loader {
    padding: pxToEm(20);

    &:after {
      @include loader;
      content: ' ';
      height: 40px;
      width: 40px;
      border-left-color: $primary;
      border-bottom-color: $primary;
      margin: auto;
    }
  }

  .app-header {
    position: fixed;
    z-index: 100;
    top: 0;
    left: 0;
    width: 100%;
    height: $app-header-height;

    a.navbar-item.is-active:not(:hover),
    .navbar-link.is-active:not(:hover) {
      background: $grey-lightest;
    }

    .navbar {
      height: 100%;
    }

    .navbar-burger,
    .navbar-brand {
      height: 100%;
    }

    .navbar-item {
      padding: pxToEm(12) pxToEm(10);

      &.is-toggle {
        height: 100%;
        padding-left: pxToEm(20);
        padding-right: pxToEm(20);
      }
    }

    .button {
      min-height: auto;
      height: 36px;
      padding-top: 6px;
      padding-bottom: 6px;
      font-weight: 500;

      &.is-flat {
        width: 40px;
      }

      &.is-warning {
        padding-left: 35px;
        padding-right: 35px;
      }
    }

    .is-avatar {
      width: 40px;
      height: 36px;
    }
  }

  // .multiselect--above .multiselect__content-wrapper {
  //   position: fixed;
  //   top: 0;
  //   left: 0;
  //   bottom: 0;
  //   right: 0;
  //   max-height: 100%;
  // }

  @include mobile() {
    .multiselect {
      display: none;
    }
  }

  @include touch() {
    .app-header .navbar-item .button {
      width: 100%;
    }

    .is-avatar {
      margin: auto;
    }

    .dropdown:not(.is-hoverable):not(.is-inline) .dropdown-menu {
      overflow: auto;
      top: auto !important;
      left: 0 !important;
      right: auto;
      bottom: 0;
      width: 100%;
      height: auto;
      max-height: 100%;
      max-width: 100%;
      padding: 0;
      transform: none;
    }

    .dropdown-content {
      position: relative;
      padding: 0;
      border-radius: 0;
    }

    a.dropdown-item,
    .dropdown .dropdown-menu .has-link a {
      white-space: normal;
    }
  }
</style>



// WEBPACK FOOTER //
// App.vue?a31fff52